import type {
  DialogFocusOutsideEvent,
  DialogPointerDownOutsideEvent,
} from '@chakra-ui/react'
import {
  Dialog as ChakraDialog,
  type DialogRootProps,
  Portal,
} from '@chakra-ui/react'
import * as React from 'react'

import { CloseButton } from './close-button'

/** Google Places Autocomplete renders `.pac-container` on `document.body` (outside dialog DOM). */
function isGooglePlacesUiTarget(
  target: EventTarget | null | undefined
): boolean {
  return target instanceof Element && Boolean(target.closest('.pac-container'))
}

/**
 * Zag dialog wires dismiss/outside handlers on the root machine, not on content DOM.
 * Passing onPointerDownOutside / onFocusOutside to Content forwards them to a native div
 * (React warns; overlay / dismiss can break).
 */
export function DialogRoot(props: DialogRootProps) {
  const { onPointerDownOutside, onFocusOutside, children, ...rest } = props

  const handlePointerDownOutside = React.useCallback(
    (e: DialogPointerDownOutsideEvent) => {
      const fromDetail = e.detail.target
      const fromPointer = e.detail.originalEvent.target
      if (
        isGooglePlacesUiTarget(fromDetail) ||
        isGooglePlacesUiTarget(fromPointer)
      ) {
        e.preventDefault()
      }
      onPointerDownOutside?.(e)
    },
    [onPointerDownOutside]
  )

  const handleFocusOutside = React.useCallback(
    (e: DialogFocusOutsideEvent) => {
      const fromDetail = e.detail.target
      const related = e.detail.originalEvent.relatedTarget
      if (
        isGooglePlacesUiTarget(fromDetail) ||
        isGooglePlacesUiTarget(related)
      ) {
        e.preventDefault()
      }
      onFocusOutside?.(e)
    },
    [onFocusOutside]
  )

  return (
    <ChakraDialog.Root
      {...rest}
      onPointerDownOutside={handlePointerDownOutside}
      onFocusOutside={handleFocusOutside}
    >
      {children}
    </ChakraDialog.Root>
  )
}

type DialogContentProps = Omit<
  ChakraDialog.ContentProps,
  'onPointerDownOutside' | 'onFocusOutside'
> & {
  portalled?: boolean
  portalRef?: React.RefObject<HTMLElement>
  backdrop?: boolean
}

export const DialogContent = React.forwardRef<
  HTMLDivElement,
  DialogContentProps
>((props, ref) => {
  const {
    children,
    portalled = true,
    portalRef,
    backdrop = true,
    zIndex: contentZIndex,
    ...rest
  } = props

  /**
   * Backdrop and positioner are siblings in the Portal. If only Content gets a
   * high z-index, the scrim stays above the positioner: the form looks dimmed
   * but stays focusable.
   */
  const stackZ = typeof contentZIndex === 'number' ? contentZIndex : undefined
  const backdropProps = {
    ...(stackZ != null ? { zIndex: stackZ } : {}),
    // Backdrop stays mounted during fade-out; disable hit-testing while data-state=closed.
    _closed: { pointerEvents: 'none' as const },
  }
  const positionerProps = {
    ...(stackZ != null ? { zIndex: stackZ + 1 } : {}),
    // Positioner can stay mounted during close transition; prevent it from swallowing clicks.
    _closed: { pointerEvents: 'none' as const },
  }

  return (
    <Portal disabled={!portalled} container={portalRef}>
      {backdrop && <ChakraDialog.Backdrop {...backdropProps} />}
      <ChakraDialog.Positioner {...positionerProps}>
        <ChakraDialog.Content ref={ref} {...rest}>
          {children}
        </ChakraDialog.Content>
      </ChakraDialog.Positioner>
    </Portal>
  )
})

export const DialogCloseTrigger = React.forwardRef<
  HTMLButtonElement,
  ChakraDialog.CloseTriggerProps
>((props, ref) => {
  return (
    <ChakraDialog.CloseTrigger
      position="absolute"
      top="2"
      insetEnd="2"
      {...props}
      asChild
    >
      <CloseButton size="sm" ref={ref}>
        {props.children}
      </CloseButton>
    </ChakraDialog.CloseTrigger>
  )
})

export const DialogFooter = ChakraDialog.Footer
export const DialogHeader = ChakraDialog.Header
export const DialogBody = ChakraDialog.Body
export const DialogBackdrop = ChakraDialog.Backdrop
export const DialogTitle = ChakraDialog.Title
export const DialogDescription = ChakraDialog.Description
export const DialogTrigger = ChakraDialog.Trigger
export const DialogActionTrigger = ChakraDialog.ActionTrigger
